import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import { resolveMediaUrl } from '../lib/social';
import { api } from '../lib/api';

/**
 * Vertical-first video player.
 *  - preload="metadata" avoids full download for off-screen videos.
 *  - IntersectionObserver toggles play/pause.
 *  - Progress bar overlay, mute toggle, tap-to-play.
 *  - Reports view milestones (started/25/50/75/100).
 */
export default function VideoPlayer({ post }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const sentMilestones = useRef(new Set());

  const media = post?.media?.[0];
  const src = resolveMediaUrl(media?.url);
  const poster = resolveMediaUrl(media?.thumbnail_url);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    const obs = new IntersectionObserver(([entry]) => {
      setVisible(entry.intersectionRatio > 0.6);
    }, { threshold: [0, 0.6, 1] });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (visible) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  }, [visible]);

  const reportProgress = async (label) => {
    if (sentMilestones.current.has(label)) return;
    sentMilestones.current.add(label);
    try { await api.post(`/posts/${post.post_id}/view`, { progress: label }); } catch (_e) { /* fine */ }
  };

  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    setProgress(pct);
    if (pct >= 25 && pct < 50) reportProgress('25');
    else if (pct >= 50 && pct < 75) reportProgress('50');
    else if (pct >= 75 && pct < 100) reportProgress('75');
    else if (pct >= 99) reportProgress('100');
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); reportProgress('started'); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div ref={containerRef} className="relative w-full bg-black overflow-hidden rounded-xl" data-testid={`video-${post.post_id}`}>
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onClick={togglePlay}
        onTimeUpdate={onTime}
        onPlay={() => reportProgress('started')}
        className="w-full max-h-[70vh] object-contain bg-black"
      />
      {!playing && (
        <button onClick={togglePlay} className="absolute inset-0 grid place-items-center bg-black/30" aria-label="play">
          <span className="grid place-items-center h-16 w-16 rounded-full bg-white/10 backdrop-blur border border-white/20">
            <Play size={26} className="text-white" />
          </span>
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
        className="absolute bottom-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white"
        aria-label="toggle sound"
        data-testid="video-mute"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
      <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-white/10">
        <div className="h-full bg-aura-cyan shadow-[0_0_8px_rgba(0,240,255,0.7)]" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
